import Label from './Label'
import Connection from './Connection'
import getIcon from './getIcon'
import TWEEN from '@tweenjs/tween.js'
import { delay } from '../helper'
import { MODE } from '../const'
import {
	LineBasicMaterial,
	MathUtils,
	Object3D,
	PerspectiveCamera,
	Raycaster,
	Scene,
	Sprite,
	TetrahedronGeometry,
	Vector2,
	Vector3,
	WebGLRenderer
} from 'three'

export default class Tetrahedron {
	private readonly mode: 'landscape' | 'portrait'
	private readonly onClickCb: (id: string) => void
	private readonly center = new Vector3()
	private readonly object = new Object3D()
	private readonly root = new Object3D()
	private readonly dolly = new Object3D()
	private readonly dollyTarget = new Object3D()
	private readonly pointer = new Vector2()
	private readonly ray = new Raycaster()
	private readonly scene = new Scene()
	private readonly camera: PerspectiveCamera
	private readonly renderer: WebGLRenderer
	private readonly labels: Label[]
	private readonly rayTargets: Sprite[]
	private readonly connections: Connection[]
	private readonly overlay: HTMLElement
	private readonly tweens = []
	private readonly el: HTMLElement
	private readonly content: HTMLElement[]
	private readonly stage: HTMLElement

	private isVisible = false
	private isAnimating = false
	private rotationStrength = 0
	private hoveredId = null
	private width: number
	private height: number
	private left: number
	private top: number

	constructor(el, mode, cb) {
		this.onClick = this.onClick.bind(this)
		this.onCloseClick = this.onCloseClick.bind(this)
		this.onPointerMove = this.onPointerMove.bind(this)

		this.mode = mode
		this.onClickCb = cb

		this.el = el
		this.content = Array.from(el.querySelectorAll('.js-tetrahedron-content'))
		this.stage = el.querySelector('.js-tetrahedron-stage')

		const { left, top, width, height } = this.stage.getBoundingClientRect()
		this.width = width
		this.height = height
		this.left = left
		this.top = top

		this.camera = new PerspectiveCamera(35, this.width / this.height, 1, 1000)
		this.renderer = new WebGLRenderer({ antialias: window.devicePixelRatio < 2, alpha: true })

		const tetraGeo = new TetrahedronGeometry(100)
		const iconSprite = getIcon()
		const labelData = JSON.parse(this.el.dataset.labels)
		const lineMaterial = new LineBasicMaterial({
			color: 'white',
			opacity: 0.5,
			transparent: true,
			depthWrite: false,
			depthTest: false
		})

		const vertices = []

		for (let i = 0; i < tetraGeo.attributes.position.count; i += 3) {
			vertices.push(
				new Vector3(
					tetraGeo.attributes.position.array[i],
					tetraGeo.attributes.position.array[i + 1],
					tetraGeo.attributes.position.array[i + 2]
				)
			)
		}

		this.labels = vertices.map((vertex, index) => {
			const { id, label } = labelData[index]
			return new Label(id, label, this.object, vertex, iconSprite.clone())
		})

		this.rayTargets = []
		this.labels.forEach(({ label, icon }) => {
			this.rayTargets.push(label)
			this.rayTargets.push(icon)
		})

		this.connections = [
			new Connection(lineMaterial, this.object, vertices[0], vertices[1]),
			new Connection(lineMaterial, this.object, vertices[1], vertices[2]),
			new Connection(lineMaterial, this.object, vertices[2], vertices[3]),
			new Connection(lineMaterial, this.object, vertices[3], vertices[0]),
			new Connection(lineMaterial, this.object, vertices[0], vertices[2]),
			new Connection(lineMaterial, this.object, vertices[1], vertices[3])
		]

		this.camera.position.z = this.mode === MODE.LANDSCAPE ? -500 : -350
		this.camera.lookAt(this.center)
		this.renderer.sortObjects = false
		this.root.visible = false

		this.stage.appendChild(this.renderer.domElement)

		this.dolly.add(this.camera)
		this.root.add(this.object)
		this.scene.add(this.dolly)
		this.scene.add(this.root)
		this.scene.add(this.dollyTarget)

		this.stage.addEventListener('click', this.onClick)

		if (this.mode === MODE.PORTRAIT) {
			this.overlay = el.querySelector('.js-tetrahedron-overlay')
			const closeBtn = el.querySelector('.js-tetrahedron-close')
			closeBtn.addEventListener('click', this.onCloseClick)
			closeBtn.classList.add('is-visible')
			this.overlay.addEventListener('touchstart', (event) => event.stopPropagation())
		}
	}

	stopTweens() {
		this.tweens.forEach((tween) => tween.stop())
		this.tweens.length = 0
	}

	show() {
		if (this.isVisible) return
		this.isVisible = true
		this.stopTweens()

		window.addEventListener('mousemove', this.onPointerMove)
		window.addEventListener('touchmove', this.onPointerMove, { passive: false })

		this.animateIn()
	}

	hide() {
		if (!this.isVisible) return
		this.isVisible = false
		this.stopTweens()

		this.root.visible = false
		this.connections.forEach((line) => line.update(0))
		this.renderer.render(this.scene, this.camera)

		window.removeEventListener('mousemove', this.onPointerMove)
		// @ts-ignore
		window.removeEventListener('touchmove', this.onPointerMove, { passive: false })

		if (this.mode === MODE.PORTRAIT) this.hideOverlay()
	}

	onPointerMove(event) {
		this.setPointer(event.touches ? event.touches[0] : event)
	}

	async onClick(event) {
		this.setPointer(event)
		this.setRay()

		if (this.hoveredId === null) return

		const item = this.labels.find((label) => label.id === this.hoveredId)
		this.labels.forEach((label) => label.setInactive())
		item.setActive()

		if (this.mode === MODE.LANDSCAPE) this.onClickCb(this.hoveredId)
		else this.showOverlay(this.hoveredId)

		this.rotateCamera(item.position.clone())
	}

	setPointer(event) {
		this.pointer.x = MathUtils.clamp(((event.clientX - this.left) / this.width) * 2 - 1, -1, 1)
		this.pointer.y = MathUtils.clamp(-((event.clientY - this.top) / this.height) * 2 + 1, -1, 1)
	}

	onCloseClick() {
		this.hideOverlay()
	}

	showOverlay(id) {
		this.content.forEach((node) => node.classList.remove('is-visible'))
		const item = this.content.find((node) => node.dataset.id === id)
		item.classList.add('is-visible')
		this.overlay.classList.add('is-visible')
	}

	hideOverlay() {
		this.overlay.classList.remove('is-visible')
	}

	async animateIn() {
		this.root.visible = true
		this.connections.forEach((line) => line.update(0))
		this.object.rotation.set(0, 0, 0)

		this.tweens.push(
			new TWEEN.Tween({ elapsed: 0 })
				.to({ elapsed: 1 }, 3000)
				.easing(TWEEN.Easing.Exponential.Out)
				.onUpdate(({ elapsed }) => {
					this.labels.forEach((label) => label.update(elapsed))
				})
				.start()
		)

		await delay(1500)

		this.tweens.push(
			new TWEEN.Tween({ elapsed: 0 })
				.to({ elapsed: 1 }, 2000)
				.easing(TWEEN.Easing.Cubic.Out)
				.onUpdate(({ elapsed }) => {
					this.connections.forEach((line) => line.update(elapsed))
				})
				.start()
		)
	}

	async rotateCamera(targetPos) {
		this.isAnimating = true
		this.rotationStrength = 0
		const startQaut = this.dolly.quaternion.clone()

		this.dollyTarget.position.copy(targetPos)
		this.dollyTarget.lookAt(this.center)

		const endQuat = this.dollyTarget.quaternion.clone()

		this.tweens.push(
			new TWEEN.Tween({ elapsed: 0 })
				.to({ elapsed: 1 }, 2000)
				.easing(TWEEN.Easing.Quadratic.InOut)
				.easing(TWEEN.Easing.Quadratic.InOut)
				.onUpdate(({ elapsed }) => {
					this.dolly.quaternion.slerpQuaternions(startQaut, endQuat, elapsed)
				})
				.start()
		)

		await delay(2000)
		this.isAnimating = false
	}

	setRay() {
		this.ray.setFromCamera(this.pointer, this.camera)
		const intersects = this.ray.intersectObjects(this.rayTargets)
		this.hoveredId = intersects[0] ? intersects[0].object.userData.id : null

		if (this.hoveredId !== null) {
			this.el.style.cursor = 'pointer'
		} else {
			this.el.style.cursor = 'auto'
		}
	}

	resize(dpr) {
		const { left, top, width, height } = this.stage.getBoundingClientRect()
		this.width = width
		this.height = height
		this.left = left
		this.top = top

		this.renderer.setSize(width, height)
		this.renderer.setPixelRatio(dpr)
		this.camera.aspect = width / height
		this.camera.updateProjectionMatrix()
	}

	update() {
		if (!this.isVisible) return

		this.renderer.render(this.scene, this.camera)

		if (!this.isAnimating) {
			this.rotationStrength += 0.01
			this.rotationStrength = Math.min(this.rotationStrength, 1)
			this.dolly.rotation.x += 0.005 * this.rotationStrength
			this.dolly.rotation.y += 0.001 * this.rotationStrength
		}

		this.setRay()
	}
}
